<script>
import { getBranchList } from '@/api/branch'
import {
  copyBusinessCalendar,
  createDateOverride,
  createPublicHoliday,
  deleteDateOverride,
  deletePublicHoliday,
  getBranchCalendar,
  getPublicHolidays,
  saveWeeklyRules,
  updateDateOverride,
  updatePublicHoliday,
} from '@/api/businessCalendar'
import { getAdminInfo } from '@/utils/token'

const DAY_OPTIONS = [
  { dayOfWeek: 1, label: '월' },
  { dayOfWeek: 2, label: '화' },
  { dayOfWeek: 3, label: '수' },
  { dayOfWeek: 4, label: '목' },
  { dayOfWeek: 5, label: '금' },
  { dayOfWeek: 6, label: '토' },
  { dayOfWeek: 7, label: '일' },
]

export default {
  name: 'BusinessCalendarPage',
  data() {
    const admin = getAdminInfo()
    const year = new Date().getFullYear()
    return {
      admin,
      year,
      selectedBranchCode: admin?.adminType === 'HQ' ? '' : admin?.branchCode || '',
      branches: [],
      publicHolidays: [],
      holidayYearOptions: [{ title: '2026년', value: 2026 }],
      weeklyRules: DAY_OPTIONS.map(d => ({ ...d, businessYn: d.dayOfWeek >= 6 ? 'N' : 'Y', memo: '' })),
      dateOverrides: [],
      holidayForm: {
        holidayDate: `${year}-01-01`,
        holidayName: '',
        holidayType: 'NATIONAL',
        displayYn: 'Y',
        memo: '',
      },
      overrideForm: {
        targetDate: `${year}-01-01`,
        businessYn: 'N',
        overrideType: 'SPECIAL_CLOSED',
        title: '',
        publicHolidayCode: '',
        memo: '',
      },
      copyForm: {
        sourceBranchCode: '',
        targetBranchCode: admin?.adminType === 'HQ' ? '' : admin?.branchCode || '',
        sourceYear: year,
        targetYear: year,
        copyWeeklyRuleYn: 'Y',
        copyOverrideYn: 'Y',
        copyPublicHolidayYn: 'Y',
        replaceTargetYn: 'Y',
        memo: '',
      },
      goldnaraCopyForm: {
        remoteBranchCode: '',
        sourceYear: year,
        targetYear: year,
        copyWeeklyRuleYn: 'Y',
        copyOverrideYn: 'Y',
        copyPublicHolidayYn: 'Y',
        replaceTargetYn: 'Y',
      },
      editingHolidayCode: '',
      editingOverrideCode: '',
      loading: false,
      saving: false,
      message: '',
      errorMsg: '',
    }
  },
  computed: {
    isHq() {
      return this.admin?.adminType === 'HQ'
    },
    branchOptions() {
      return this.branches.map(b => ({
        title: `${b.branchName || b.branchCode} (${this.branchTypeLabel(b.branchType)})`,
        value: b.branchCode,
      }))
    },
    selectedBranchName() {
      return this.branches.find(b => b.branchCode === this.selectedBranchCode)?.branchName || this.selectedBranchCode
    },
  },
  async mounted() {
    await this.fetchBranches()
    await this.fetchHolidayYearOptions()
    if (!this.selectedBranchCode && this.branches.length) {
      const hq = this.branches.find(b => String(b.branchType).toUpperCase() === 'HQ')
      this.selectedBranchCode = hq?.branchCode || this.branches[0].branchCode
      this.copyForm.targetBranchCode = this.selectedBranchCode
    }
    await this.fetchAll()
  },
  methods: {
    resetHolidayForm() {
      this.editingHolidayCode = ''
      this.holidayForm = { holidayDate: `${this.year}-01-01`, holidayName: '', holidayType: 'NATIONAL', displayYn: 'Y', memo: '' }
    },
    resetOverrideForm() {
      this.editingOverrideCode = ''
      this.overrideForm = { targetDate: `${this.year}-01-01`, businessYn: 'N', overrideType: 'SPECIAL_CLOSED', title: '', publicHolidayCode: '', memo: '' }
    },
    async fetchBranches() {
      const res = await getBranchList({ page: 1, size: 500 })
      if (res.data?.code === 0) {
        this.branches = res.data?.data?.items || []
      }
    },
    async fetchHolidayYearOptions() {
      try {
        const res = await getPublicHolidays()
        const rows = res.data?.code === 0 ? (res.data.items || []) : []
        const years = new Set([2026])
        rows.forEach(item => {
          const holidayYear = Number(String(item.holidayDate || '').slice(0, 4))
          if (Number.isFinite(holidayYear) && holidayYear >= 2026) years.add(holidayYear)
        })
        years.add(Number(this.year) || new Date().getFullYear())
        years.add(Number(this.copyForm.sourceYear) || Number(this.year) || 2026)
        years.add(Number(this.copyForm.targetYear) || Number(this.year) || 2026)
        this.holidayYearOptions = [...years]
          .sort((a, b) => a - b)
          .map(year => ({ title: `${year}년`, value: year }))
      } catch (_e) {
        const year = Number(this.year) || 2026
        this.holidayYearOptions = [...new Set([2026, year])]
          .sort((a, b) => a - b)
          .map(y => ({ title: `${y}년`, value: y }))
      }
    },
    async fetchAll() {
      this.loading = true
      this.errorMsg = ''
      try {
        const holidayRes = await getPublicHolidays(this.year)
        if (holidayRes.data?.code === 0) {
          this.publicHolidays = holidayRes.data.items || []
        }
        if (this.selectedBranchCode) {
          const calendarRes = await getBranchCalendar(this.selectedBranchCode, this.year)
          if (calendarRes.data?.code === 0) {
            this.dateOverrides = calendarRes.data.dateOverrides || []
            this.applyWeeklyRules(calendarRes.data.weeklyRules || [])
          } else {
            this.dateOverrides = []
            this.errorMsg = calendarRes.data?.message || '영업일 정보를 불러오지 못했습니다.'
          }
        }
      } catch (e) {
        console.error('[BusinessCalendarPage] fetchAll error:', e)
        this.errorMsg = '영업일 정보를 불러오지 못했습니다.'
      } finally {
        this.loading = false
      }
    },
    applyWeeklyRules(rows) {
      this.weeklyRules = DAY_OPTIONS.map(day => {
        const found = rows.find(r => Number(r.dayOfWeek) === day.dayOfWeek)
        return { ...day, businessYn: found?.businessYn || (day.dayOfWeek >= 6 ? 'N' : 'Y'), memo: found?.memo || '' }
      })
    },
    async saveHoliday() {
      if (!this.isHq) return
      await this.runSave(async () => {
        const res = this.editingHolidayCode
          ? await updatePublicHoliday(this.editingHolidayCode, this.holidayForm)
          : await createPublicHoliday(this.holidayForm)
        this.assertOk(res)
        this.resetHolidayForm()
      })
    },
    editHoliday(item) {
      this.editingHolidayCode = item.holidayCode
      this.holidayForm = {
        holidayDate: item.holidayDate,
        holidayName: item.holidayName,
        holidayType: item.holidayType || 'NATIONAL',
        displayYn: item.displayYn || 'Y',
        memo: item.memo || '',
      }
    },
    async removeHoliday(item) {
      if (!this.isHq) return
      await this.runSave(async () => this.assertOk(await deletePublicHoliday(item.holidayCode)))
    },
    async saveWeekly() {
      await this.runSave(async () => {
        this.assertOk(await saveWeeklyRules(this.selectedBranchCode, {
          year: this.year,
          rules: this.weeklyRules.map(r => ({ dayOfWeek: r.dayOfWeek, businessYn: r.businessYn, memo: r.memo || '' })),
        }))
      })
    },
    async saveOverride() {
      await this.runSave(async () => {
        const res = this.editingOverrideCode
          ? await updateDateOverride(this.selectedBranchCode, this.editingOverrideCode, this.overrideForm)
          : await createDateOverride(this.selectedBranchCode, this.overrideForm)
        this.assertOk(res)
        this.resetOverrideForm()
      })
    },
    editOverride(item) {
      this.editingOverrideCode = item.overrideCode
      this.overrideForm = {
        targetDate: item.targetDate,
        businessYn: item.businessYn || 'N',
        overrideType: item.overrideType || 'SPECIAL_CLOSED',
        title: item.title || '',
        publicHolidayCode: item.publicHolidayCode || '',
        memo: item.memo || '',
      }
    },
    async removeOverride(item) {
      await this.runSave(async () => this.assertOk(await deleteDateOverride(this.selectedBranchCode, item.overrideCode)))
    },
    async copyCalendar() {
      await this.runSave(async () => {
        this.assertOk(await copyBusinessCalendar(this.copyForm))
      })
    },
    async copyFromGoldnara() {
      if (!this.goldnaraCopyForm.remoteBranchCode) {
        this.errorMsg = '골드나라 지점코드를 입력해 주세요.'
        return
      }
      await this.runSave(async () => {
        this.assertOk(await copyBusinessCalendar({
          sourceBranchCode: this.goldnaraCopyForm.remoteBranchCode,
          targetBranchCode: this.selectedBranchCode,
          sourceYear: this.goldnaraCopyForm.sourceYear,
          targetYear: this.goldnaraCopyForm.targetYear,
          copyWeeklyRuleYn: this.goldnaraCopyForm.copyWeeklyRuleYn,
          copyOverrideYn: this.goldnaraCopyForm.copyOverrideYn,
          copyPublicHolidayYn: this.goldnaraCopyForm.copyPublicHolidayYn,
          replaceTargetYn: this.goldnaraCopyForm.replaceTargetYn,
          copyDirection: 'GOLDNARA_TO_AURA',
          sourceSystem: 'GOLDNARA',
          targetSystem: 'AURA',
        }))
      })
    },
    async copyToGoldnara() {
      if (!this.goldnaraCopyForm.remoteBranchCode) {
        this.errorMsg = '골드나라 지점코드를 입력해 주세요.'
        return
      }
      await this.runSave(async () => {
        this.assertOk(await copyBusinessCalendar({
          sourceBranchCode: this.selectedBranchCode,
          targetBranchCode: this.goldnaraCopyForm.remoteBranchCode,
          sourceYear: this.goldnaraCopyForm.sourceYear,
          targetYear: this.goldnaraCopyForm.targetYear,
          copyWeeklyRuleYn: this.goldnaraCopyForm.copyWeeklyRuleYn,
          copyOverrideYn: this.goldnaraCopyForm.copyOverrideYn,
          copyPublicHolidayYn: this.goldnaraCopyForm.copyPublicHolidayYn,
          replaceTargetYn: this.goldnaraCopyForm.replaceTargetYn,
          copyDirection: 'AURA_TO_GOLDNARA',
          sourceSystem: 'AURA',
          targetSystem: 'GOLDNARA',
        }))
      })
    },
    async runSave(work) {
      this.saving = true
      this.errorMsg = ''
      this.message = ''
      try {
        await work()
        this.message = '저장되었습니다.'
        await this.fetchHolidayYearOptions()
        await this.fetchAll()
      } catch (e) {
        this.errorMsg = e?.message || '저장 중 오류가 발생했습니다.'
      } finally {
        this.saving = false
      }
    },
    assertOk(res) {
      if (res.data?.code !== 0) throw new Error(res.data?.message || '처리하지 못했습니다.')
    },
    branchTypeLabel(type) {
      return ({ HQ: '본점', DIRECT: '직영점', AGENCY: '대리점', FRANCHISE: '가맹점' })[type] || type
    },
    businessLabel(yn) {
      return yn === 'Y' ? '영업' : '휴무'
    },
  },
  watch: {
    year() {
      this.copyForm.sourceYear = this.year
      this.copyForm.targetYear = this.year
      this.fetchAll()
    },
    selectedBranchCode(value) {
      this.copyForm.targetBranchCode = value
      this.fetchAll()
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-5">
      <div>
        <div class="text-h5 font-weight-bold">영업일 관리</div>
        <div class="text-body-2 text-grey mt-1">공휴일 표시와 본점/지점별 실제 영업일을 관리합니다.</div>
      </div>
      <v-btn color="primary" variant="tonal" prepend-icon="mdi-refresh" :loading="loading" @click="fetchAll">새로고침</v-btn>
    </div>

    <v-alert v-if="message" type="success" variant="tonal" density="compact" class="mb-4">{{ message }}</v-alert>
    <v-alert v-if="errorMsg" type="error" variant="tonal" density="compact" class="mb-4">{{ errorMsg }}</v-alert>

    <v-card rounded="lg" elevation="1" class="mb-5">
      <v-card-text>
        <v-row>
          <v-col cols="12" md="3">
            <v-text-field v-model.number="year" type="number" label="연도" variant="outlined" density="compact" hide-details />
          </v-col>
          <v-col cols="12" md="6">
            <v-select v-model="selectedBranchCode" :items="branchOptions" label="영업일 관리 대상" variant="outlined" density="compact" hide-details />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-row>
      <v-col cols="12" lg="5">
        <v-card rounded="lg" elevation="1">
          <v-card-title class="text-subtitle-1 font-weight-bold">공통 공휴일 표시</v-card-title>
          <v-card-text>
            <v-row v-if="isHq" dense>
              <v-col cols="12" sm="4"><v-text-field v-model="holidayForm.holidayDate" label="일자" type="date" density="compact" variant="outlined" /></v-col>
              <v-col cols="12" sm="5"><v-text-field v-model="holidayForm.holidayName" label="공휴일명" density="compact" variant="outlined" /></v-col>
              <v-col cols="12" sm="3"><v-btn block color="primary" :loading="saving" @click="saveHoliday">{{ editingHolidayCode ? '수정' : '추가' }}</v-btn></v-col>
              <v-col cols="12"><v-text-field v-model="holidayForm.memo" label="메모" density="compact" variant="outlined" /></v-col>
            </v-row>
            <v-table density="compact">
              <thead><tr><th>일자</th><th>명칭</th><th v-if="isHq" class="text-right">관리</th></tr></thead>
              <tbody>
                <tr v-for="item in publicHolidays" :key="item.holidayCode">
                  <td>{{ item.holidayDate }}</td>
                  <td>{{ item.holidayName }}</td>
                  <td v-if="isHq" class="text-right">
                    <v-btn size="x-small" variant="text" @click="editHoliday(item)">수정</v-btn>
                    <v-btn size="x-small" variant="text" color="error" @click="removeHoliday(item)">삭제</v-btn>
                  </td>
                </tr>
                <tr v-if="publicHolidays.length === 0"><td :colspan="isHq ? 3 : 2" class="text-center text-grey py-6">등록된 공휴일이 없습니다.</td></tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" lg="7">
        <v-card rounded="lg" elevation="1" class="mb-5">
          <v-card-title class="text-subtitle-1 font-weight-bold">{{ selectedBranchName }} 반복 영업일</v-card-title>
          <v-card-text>
            <div class="d-flex flex-wrap ga-2 mb-4">
              <v-btn v-for="rule in weeklyRules" :key="rule.dayOfWeek" :color="rule.businessYn === 'Y' ? 'success' : 'grey'" variant="tonal" @click="rule.businessYn = rule.businessYn === 'Y' ? 'N' : 'Y'">
                {{ rule.label }} {{ businessLabel(rule.businessYn) }}
              </v-btn>
            </div>
            <v-btn color="primary" :loading="saving" @click="saveWeekly">반복 규칙 저장</v-btn>
          </v-card-text>
        </v-card>

        <v-card rounded="lg" elevation="1">
          <v-card-title class="text-subtitle-1 font-weight-bold">특정일 예외</v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="12" sm="3"><v-text-field v-model="overrideForm.targetDate" label="일자" type="date" density="compact" variant="outlined" /></v-col>
              <v-col cols="12" sm="3"><v-select v-model="overrideForm.businessYn" :items="[{title:'영업', value:'Y'}, {title:'휴무', value:'N'}]" label="구분" density="compact" variant="outlined" /></v-col>
              <v-col cols="12" sm="4"><v-text-field v-model="overrideForm.title" label="제목" density="compact" variant="outlined" /></v-col>
              <v-col cols="12" sm="2"><v-btn block color="primary" :loading="saving" @click="saveOverride">{{ editingOverrideCode ? '수정' : '추가' }}</v-btn></v-col>
              <v-col cols="12"><v-text-field v-model="overrideForm.memo" label="메모" density="compact" variant="outlined" /></v-col>
            </v-row>
            <v-table density="compact">
              <thead><tr><th>일자</th><th>구분</th><th>제목</th><th class="text-right">관리</th></tr></thead>
              <tbody>
                <tr v-for="item in dateOverrides" :key="item.overrideCode">
                  <td>{{ item.targetDate }}</td>
                  <td><v-chip size="small" :color="item.businessYn === 'Y' ? 'success' : 'error'" variant="tonal">{{ businessLabel(item.businessYn) }}</v-chip></td>
                  <td>{{ item.title || item.memo || '-' }}</td>
                  <td class="text-right">
                    <v-btn size="x-small" variant="text" @click="editOverride(item)">수정</v-btn>
                    <v-btn size="x-small" variant="text" color="error" @click="removeOverride(item)">삭제</v-btn>
                  </td>
                </tr>
                <tr v-if="dateOverrides.length === 0"><td colspan="4" class="text-center text-grey py-6">등록된 예외일이 없습니다.</td></tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card rounded="lg" elevation="1" class="mt-5">
      <v-card-title class="text-subtitle-1 font-weight-bold">영업일 복사</v-card-title>
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="2">
            <v-select v-model="copyForm.sourceYear" :items="holidayYearOptions" label="원본년도" variant="outlined" density="compact" />
          </v-col>
          <v-col cols="12" md="10">
            <v-select v-model="copyForm.sourceBranchCode" :items="branchOptions" label="원본영업점" variant="outlined" density="compact" />
          </v-col>
          <v-col cols="12" md="2">
            <v-select v-model="copyForm.targetYear" :items="holidayYearOptions" label="대상년도" variant="outlined" density="compact" />
          </v-col>
          <v-col cols="12" md="10">
            <v-select v-model="copyForm.targetBranchCode" :items="branchOptions" label="대상영업점" variant="outlined" density="compact" />
          </v-col>
          <v-col cols="12" class="d-flex align-center">
            <v-checkbox v-model="copyForm.copyPublicHolidayYn" true-value="Y" false-value="N" label="공통 공휴일" hide-details />
            <v-checkbox v-model="copyForm.replaceTargetYn" true-value="Y" false-value="N" label="대상 연도 기존 설정 교체" hide-details />
          </v-col>
        </v-row>
        <v-btn color="primary" prepend-icon="mdi-content-copy" :loading="saving" @click="copyCalendar">복사</v-btn>
      </v-card-text>
    </v-card>

    <v-card v-if="isHq" rounded="lg" elevation="1" class="mt-5">
      <v-card-title class="text-subtitle-1 font-weight-bold">골드나라 영업일 연동</v-card-title>
      <v-card-text>
        <p class="text-caption text-medium-emphasis mb-3">현재 선택된 아우라 영업점과 골드나라 지점 간 영업일 설정을 API로 복사합니다.</p>
        <v-row dense>
          <v-col cols="12" md="5">
            <v-text-field v-model="goldnaraCopyForm.remoteBranchCode" label="골드나라 지점코드" variant="outlined" density="compact" />
          </v-col>
          <v-col cols="12" md="2">
            <v-select v-model="goldnaraCopyForm.sourceYear" :items="holidayYearOptions" label="원본년도" variant="outlined" density="compact" />
          </v-col>
          <v-col cols="12" md="2">
            <v-select v-model="goldnaraCopyForm.targetYear" :items="holidayYearOptions" label="대상년도" variant="outlined" density="compact" />
          </v-col>
          <v-col cols="12" md="3" class="d-flex align-center">
            <v-checkbox v-model="goldnaraCopyForm.replaceTargetYn" true-value="Y" false-value="N" label="대상 기존 설정 교체" hide-details />
          </v-col>
          <v-col cols="12" class="d-flex flex-wrap ga-3">
            <v-checkbox v-model="goldnaraCopyForm.copyWeeklyRuleYn" true-value="Y" false-value="N" label="주간 규칙" hide-details />
            <v-checkbox v-model="goldnaraCopyForm.copyOverrideYn" true-value="Y" false-value="N" label="특정일 예외" hide-details />
            <v-checkbox v-model="goldnaraCopyForm.copyPublicHolidayYn" true-value="Y" false-value="N" label="공통 공휴일" hide-details />
          </v-col>
        </v-row>
        <div class="d-flex flex-wrap ga-2 mt-2">
          <v-btn color="primary" prepend-icon="mdi-download" :loading="saving" @click="copyFromGoldnara">골드나라에서 복사</v-btn>
          <v-btn color="secondary" variant="tonal" prepend-icon="mdi-upload" :loading="saving" @click="copyToGoldnara">골드나라로 복사</v-btn>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>
